import type { Participant } from "@worktools/shared";
import { getAuthToken, getDevIdentity } from "../api/client";

export type LiveBoard = {
  boardId: string;
  boardName: string;
  owner: { id: string; name: string; avatarUrl: string | null };
  startedAt: number;
  participants: number;
  joined: boolean;
  pending: boolean;
};

export type JoinRequest = { userId: string; name: string; avatarUrl: string | null; at: number };

export type LobbyEvents = {
  onLive: (sessions: LiveBoard[]) => void;
  onRequests: (boardId: string, requests: JoinRequest[]) => void;
  onJoinResult: (boardId: string, approved: boolean) => void;
  onSessionEnded: (boardId: string) => void;
};

export type LobbyConnection = {
  shareStart: (boardId: string, boardName: string) => void;
  shareStop: (boardId: string) => void;
  requestJoin: (boardId: string) => void;
  approve: (boardId: string, userId: string) => void;
  deny: (boardId: string, userId: string) => void;
  close: () => void;
};

function lobbyUrl(channelId: string, me: Participant): string {
  const protocol = location.protocol === "https:" ? "wss:" : "ws:";
  const params = new URLSearchParams({ name: me.name });
  const token = getAuthToken();
  if (token) params.set("token", token);
  const dev = getDevIdentity();
  if (dev) params.set("devUser", dev.id);
  if (me.avatarUrl) params.set("avatar", me.avatarUrl);
  return `${protocol}//${location.host}/ws/lobby/${encodeURIComponent(channelId)}?${params}`;
}

/** Channel-wide side channel for live boards and join requests. */
export function connectLobby(channelId: string, me: Participant, events: LobbyEvents): LobbyConnection {
  let socket = new WebSocket(lobbyUrl(channelId, me));
  let closed = false;
  let retry: ReturnType<typeof setTimeout> | null = null;

  const bind = () => {
    socket.onmessage = (event) => {
      const message = JSON.parse(event.data as string);
      if (message.type === "live") events.onLive(message.sessions as LiveBoard[]);
      if (message.type === "requests") events.onRequests(message.boardId, message.requests);
      if (message.type === "join_result") events.onJoinResult(message.boardId, message.approved);
      if (message.type === "session_ended") events.onSessionEnded(message.boardId);
    };
    socket.onclose = () => {
      if (closed) return;
      retry = setTimeout(() => {
        socket = new WebSocket(lobbyUrl(channelId, me));
        bind();
      }, 1500);
    };
  };
  bind();

  const send = (payload: unknown) => {
    if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify(payload));
  };

  return {
    shareStart: (boardId, boardName) => send({ type: "share_start", boardId, boardName }),
    shareStop: (boardId) => send({ type: "share_stop", boardId }),
    requestJoin: (boardId) => send({ type: "join_request", boardId }),
    approve: (boardId, userId) => send({ type: "join_approve", boardId, userId }),
    deny: (boardId, userId) => send({ type: "join_deny", boardId, userId }),
    close: () => {
      closed = true;
      if (retry) clearTimeout(retry);
      socket.close();
    },
  };
}
